import type { ButtonHTMLAttributes, ReactNode } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Database,
  LoaderCircle,
  TriangleAlert,
} from "lucide-react";

export function SectionHeader({
  eyebrow,
  title,
  desc,
  action,
  className = "",
}: {
  eyebrow: string;
  title: string;
  desc?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-end justify-between gap-4 ${className}`}>
      <div className="min-w-0">
        <p className="eyebrow text-clay">{eyebrow}</p>
        <h2 className="mt-1.5 font-display text-xl font-medium tracking-tight text-ink sm:text-2xl">
          {title}
        </h2>
        {desc && <p className="mt-1 text-[13px] text-ink-soft">{desc}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function Panel({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-sand-light/80 bg-paper shadow-[0_1px_2px_rgba(46,42,37,0.04),0_8px_24px_-12px_rgba(46,42,37,0.08)] ${className}`}
    >
      {children}
    </div>
  );
}

export function StatusDot({
  tone,
  label,
}: {
  tone: "ok" | "warn" | "down" | "idle";
  label: string;
}) {
  const colors = {
    ok: "bg-sage",
    warn: "bg-clay",
    down: "bg-destructive",
    idle: "bg-ink-faint",
  };
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] text-ink-soft">
      <span className={`h-1.5 w-1.5 rounded-full ${colors[tone]}`} />
      {label}
    </span>
  );
}

export function Tag({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "clay" | "sage" | "danger";
}) {
  const tones = {
    neutral: "bg-cream-deep text-ink-soft",
    clay: "bg-clay-soft text-clay-deep",
    sage: "bg-[#E7EBE0] text-sage",
    danger: "bg-[#F3DDD3] text-destructive",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export function SandArcs({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 600 320"
      fill="none"
      aria-hidden
      className={`pointer-events-none select-none ${className}`}
    >
      <circle
        cx="480"
        cy="40"
        r="180"
        stroke="#DEC9B8"
        strokeWidth="1"
        opacity="0.7"
      />
      <circle
        cx="480"
        cy="40"
        r="130"
        stroke="#DEC9B8"
        strokeWidth="1"
        opacity="0.5"
      />
      <circle cx="480" cy="40" r="80" fill="#F5ECE3" opacity="0.9" />
      <path
        d="M-20 260 C 140 180, 260 330, 420 250 S 620 160, 660 220"
        stroke="#C15F3C"
        strokeWidth="1.5"
        opacity="0.35"
      />
      <path
        d="M-20 290 C 160 220, 280 360, 440 280"
        stroke="#DEC9B8"
        strokeWidth="1.5"
        opacity="0.8"
      />
      <circle cx="150" cy="255" r="5" fill="#C15F3C" opacity="0.55" />
    </svg>
  );
}

export function BrandMark({ size = 34 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden>
      <rect width="40" height="40" rx="12" fill="#2E2A25" />
      <path
        d="M10 25.5c0-3.6 2.9-6.5 6.5-6.5.5 0 1 .05 1.5.16C19 15.6 22.6 13 26.8 13c3.9 0 7.2 3 7.2 6.9 0 .3-.03.6-.08.9"
        stroke="#FAF6F0"
        strokeWidth="1.8"
        strokeLinecap="round"
        transform="translate(-2 1.5)"
      />
      <circle cx="27.5" cy="27" r="2.4" fill="#C15F3C" />
    </svg>
  );
}

export function Pagination({
  page,
  pages,
  total,
  onChange,
  className = "",
}: {
  page: number;
  pages: number;
  total?: number;
  onChange: (page: number) => void;
  className?: string;
}) {
  if (pages <= 1 && total === undefined) return null;
  const visible = compactPages(page, pages);
  const buttonClass =
    "flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-[12px] font-medium transition duration-200 active:scale-95 disabled:pointer-events-none disabled:opacity-30";
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 ${className}`}
    >
      <span className="text-[11.5px] text-ink-faint">
        {total === undefined ? `${pages} 页` : `共 ${total} 条`}
      </span>
      <div className="flex items-center justify-center gap-1">
        <button
          aria-label="上一页"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className={`${buttonClass} text-ink-soft hover:bg-peach`}
        >
          <ChevronLeft size={14} />
        </button>
        {visible.map((item, index) =>
          item === "ellipsis" ? (
            <span
              key={`ellipsis-${index}`}
              className="flex h-8 min-w-6 items-center justify-center text-[12px] text-ink-faint"
            >
              ···
            </span>
          ) : (
            <button
              key={item}
              onClick={() => onChange(item)}
              aria-current={item === page ? "page" : undefined}
              className={`${buttonClass} ${item === page ? "bg-ink text-paper" : "text-ink-soft hover:bg-peach"}`}
            >
              {item}
            </button>
          ),
        )}
        <button
          aria-label="下一页"
          disabled={page >= pages || pages === 0}
          onClick={() => onChange(page + 1)}
          className={`${buttonClass} text-ink-soft hover:bg-peach`}
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

function compactPages(page: number, pages: number): Array<number | "ellipsis"> {
  if (pages <= 7) return Array.from({ length: pages }, (_, index) => index + 1);
  const values = new Set([1, pages, page - 1, page, page + 1]);
  const sorted = [...values]
    .filter((value) => value >= 1 && value <= pages)
    .sort((a, b) => a - b);
  const result: Array<number | "ellipsis"> = [];
  sorted.forEach((value, index) => {
    if (index > 0 && value - sorted[index - 1] > 1) result.push("ellipsis");
    result.push(value);
  });
  return result;
}

export function ActionButton({
  children,
  tone = "light",
  busy = false,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: "dark" | "light" | "danger" | "ghost";
  busy?: boolean;
}) {
  const tones = {
    dark: "border-ink bg-ink text-paper hover:bg-clay-deep hover:border-clay-deep",
    light:
      "border-sand bg-paper text-ink-soft hover:text-ink hover:border-clay/50",
    danger: "border-[#E8C9B8] bg-paper text-destructive hover:bg-[#F9E9E1]",
    ghost:
      "border-transparent bg-transparent text-ink-soft hover:bg-peach hover:text-ink",
  };
  return (
    <button
      {...props}
      disabled={props.disabled || busy}
      className={`inline-flex min-h-9 items-center justify-center gap-2 rounded-full border px-4 py-2 text-[12.5px] font-medium transition duration-200 active:scale-95 disabled:pointer-events-none disabled:opacity-45 ${tones[tone]} ${className}`}
    >
      {busy && <LoaderCircle size={14} className="animate-spin" />}
      {children}
    </button>
  );
}

export function SearchBox({
  value,
  onChange,
  onSubmit,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  placeholder: string;
}) {
  return (
    <form
      className="flex min-w-52 flex-1 items-center gap-2 rounded-full border border-sand-light bg-paper px-4 py-2 text-[13px] sm:max-w-xs"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit?.();
      }}
    >
      <span className="text-ink-faint">⌕</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent outline-none placeholder:text-ink-faint/70"
      />
    </form>
  );
}

export function LoadingState({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-3 p-6" aria-label="正在加载">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="h-3 animate-pulse rounded-full bg-cream-deep"
          style={{ width: `${92 - (index % 3) * 13}%` }}
        />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-peach text-clay-deep">
        <Database size={18} />
      </span>
      <strong className="mt-3 text-[14px] font-medium text-ink">{title}</strong>
      <p className="mt-1 max-w-sm text-[12.5px] text-ink-faint">
        {description}
      </p>
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
}: {
  error: Error;
  onRetry?: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[#E8C9B8] bg-[#FBEEE8] p-4 text-destructive">
      <TriangleAlert size={17} className="shrink-0" />
      <div className="min-w-0 flex-1">
        <strong className="text-[13px]">数据读取失败</strong>
        <p className="mt-0.5 text-[12px] opacity-80">{error.message}</p>
      </div>
      {onRetry && (
        <ActionButton tone="ghost" onClick={onRetry}>
          重试
        </ActionButton>
      )}
    </div>
  );
}
